import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';

import {
  LmsPlacement,
  LmsRegistration,
  LmsRegistryService,
  MoodlePlacementActivityGroup,
  MoodleCourse,
  RemotePlacementBulkTarget,
  RemotePlacementCreate,
} from '../../../services/lms-registry.service';
import { Tutor } from '../../../services/tutor.service';
import { DozentService } from '../../../services/dozent.service';

interface ActivityPlacementGroupState extends MoodlePlacementActivityGroup {
  enabled: boolean;
  botId: string;
  expanded: boolean;
}

@Component({
  selector: 'app-placement-create-dialog',
  templateUrl: './placement-create-dialog.component.html',
  styleUrl: './placement-create-dialog.component.scss',
})
export class PlacementCreateDialogComponent implements OnInit {
  @Input() site!: LmsRegistration;
  @Input() course!: MoodleCourse;
  @Input() placement?: LmsPlacement;
  @Input() tutors: Tutor[] = [];

  form!: FormGroup;
  saving = false;
  refreshingCourse = false;
  loadingActivities = false;
  errorDetail: string | null = null;
  showAdvancedPositioning = false;
  activityGroups: ActivityPlacementGroupState[] = [];
  readonly inheritCourseAgentValue = '__inherit_course_agent__';

  /** Hard-coded options that mirror what the Moodle plugin understands.
   * Spec lists side-pre/side-post and course-view-* / course-* page
   * patterns as the defaults. */
  readonly regionOptions = [
    { value: 'side-pre', label: 'side-pre' },
    { value: 'side-post', label: 'side-post' },
  ];

  readonly pagePatternOptions = [
    { value: 'course-view-*', label: 'course-view-*' },
    { value: 'course-*', label: 'course-*' },
  ];

  constructor(
    private fb: FormBuilder,
    private lms: LmsRegistryService,
    private toastr: NbToastrService,
    private dialogRef: NbDialogRef<PlacementCreateDialogComponent>,
    private t: TranslateService,
    private dozentService: DozentService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      bot_id: [this.placement?.bot_id || null],
      course_enabled: [true],
      page_pattern: ['course-view-*'],
      block_region: ['side-pre'],
      block_weight: [0],
    });
    this.loadActivities();
  }

  tutorById(botId: string | null | undefined): Tutor | undefined {
    if (!botId) return undefined;
    return this.tutors.find((t) => t.id === botId);
  }

  tutorVisualName(tutor: Tutor | null | undefined): string | null {
    const visualName = (tutor?.visual_name || '').trim();
    if (!visualName) return null;

    const internalName = (tutor?.name || '').trim();
    return visualName === internalName ? null : visualName;
  }

  tutorFullLabel(tutor: Tutor | null | undefined): string {
    if (!tutor) return '';
    const visualName = this.tutorVisualName(tutor);
    return visualName ? `${tutor.name} · ${visualName}` : tutor.name;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  toggleAdvancedPositioning(): void {
    this.showAdvancedPositioning = !this.showAdvancedPositioning;
  }

  toggleGroup(group: ActivityPlacementGroupState, enabled: boolean): void {
    group.enabled = enabled;
  }

  toggleGroupExpanded(group: ActivityPlacementGroupState): void {
    group.expanded = !group.expanded;
  }

  effectiveGroupTutor(group: ActivityPlacementGroupState): string | null {
    return this.groupHasTutorOverride(group)
      ? group.botId
      : this.form.value.bot_id || null;
  }

  groupHasTutorOverride(group: ActivityPlacementGroupState): boolean {
    return !!group.botId && group.botId !== this.inheritCourseAgentValue;
  }

  /** Anzeigename der Aktivitätsgruppe. Das Backend liefert einen englischen
   *  Sammelbegriff („Quizzes", „Databases"); übersetzt wird hier, mit dem
   *  Backend-Wert als Rückfall für Typen ohne eigenen Schlüssel. */
  groupLabel(group: ActivityPlacementGroupState): string {
    const key = `pages.tutors.placements.lms.createDialog.activityTypes.${group.modtype}`;
    const translated = this.t.instant(key);
    return translated === key ? group.label : translated;
  }

  activityPagePattern(modtype: string | null | undefined): string {
    const cleaned = (modtype || 'activity').trim().toLowerCase() || 'activity';
    return `mod-${cleaned}-*`;
  }

  private placementId(placement: LmsPlacement | null | undefined): string | null {
    return placement?.moodle_placement_uuid || placement?.id || null;
  }

  private hasExistingPlacementState(): boolean {
    return !!this.placement || this.activityGroups.some((group) =>
      group.activities.some((activity) => !!activity.placement),
    );
  }

  totalActivityCount(): number {
    return this.activityGroups.reduce((sum, group) => sum + group.activities.length, 0);
  }

  selectedActivityCount(): number {
    return this.activityGroups
      .filter((group) => group.enabled)
      .reduce((sum, group) => sum + group.activities.length, 0);
  }

  private loadActivities(): void {
    this.loadingActivities = true;
    this.lms.listSiteCourseActivities(this.site.id, this.course.id, { refresh: true }).subscribe({
      next: (resp) => {
        const hasAnyModulePlacement = (resp.activities || []).some((activity) => !!activity.placement);
        const hasExistingPlacementState = !!this.placement || hasAnyModulePlacement;
        const courseBotId = this.placement?.bot_id || this.form.value.bot_id || null;
        this.activityGroups = (resp.groups || []).map((group) => {
          const placedActivities = group.activities.filter((activity) => !!activity.placement);
          const placedBotIds = Array.from(new Set(
            placedActivities
              .map((activity) => activity.placement?.bot_id || null)
              .filter((botId): botId is string => !!botId),
          ));
          const explicitBotId = placedBotIds.length === 1 && placedBotIds[0] !== courseBotId
            ? placedBotIds[0]
            : this.inheritCourseAgentValue;
          return {
            ...group,
            // Bestehende Platzierung: zeigen, was wirklich platziert ist.
            // Neue: alles vorauswählen, außer Gruppen, die das Backend als
            // Opt-in meldet (Foren — jeder Kurs hat ein Ankündigungsforum).
            enabled: hasExistingPlacementState
              ? placedActivities.length > 0
              : group.default_enabled !== false,
            botId: explicitBotId,
            expanded: placedActivities.length > 0,
          };
        });
        this.loadingActivities = false;
      },
      error: () => {
        this.activityGroups = [];
        this.loadingActivities = false;
      },
    });
  }

  refreshCourse(): void {
    const courseUuid = this.course.uuid;
    if (!courseUuid || this.refreshingCourse) return;
    this.refreshingCourse = true;
    this.dozentService.refreshMoodleCourse(courseUuid).subscribe({
      next: () => {
        this.refreshingCourse = false;
        this.toastr.success(
          this.t.instant('pages.tutors.placements.lms.rowActions.refreshCourseSuccess'),
          '',
        );
        this.loadActivities();
      },
      error: (err) => {
        this.refreshingCourse = false;
        const detail = err?.error?.detail;
        this.toastr.danger(
          typeof detail === 'string'
            ? detail
            : this.t.instant('pages.tutors.placements.lms.rowActions.refreshCourseError'),
          '',
        );
      },
    });
  }

  private codeToI18nKey(code: string): string {
    return code.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  }

  private resolveError(err: any): string {
    const detail = err?.error?.detail;

    if (detail === 'remote_placement_disabled_by_site_admin') {
      return this.t.instant('pages.tutors.placements.lms.errors.remoteDisabled');
    }
    if (detail === 'course_not_found_in_moodle') {
      return this.t.instant('pages.tutors.placements.lms.errors.courseNotFound');
    }

    if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
      const key = `pages.tutors.placements.lms.errors.${this.codeToI18nKey(detail.code)}`;
      const translated = this.t.instant(key, {
        url: detail.url || '',
        status: detail.status ?? '',
      });
      if (translated !== key) return translated;
      return detail.message || this.t.instant('toasts.error');
    }

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    return err?.message || this.t.instant('toasts.error');
  }

  save(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;
    this.errorDetail = null;

    const v = this.form.value;
    const targets: RemotePlacementBulkTarget[] = [];
    const blockRegion = v.block_region || 'side-pre';
    const blockWeight = Number(v.block_weight) || 0;
    const placementId = this.placement?.moodle_placement_uuid || this.placement?.id || null;
    const requests: Array<Observable<unknown>> = [];
    if (this.placement && placementId && v.course_enabled && v.bot_id !== this.placement.bot_id) {
      requests.push(this.lms.updatePlacement(placementId, { bot_id: v.bot_id }));
    }

    if (v.course_enabled && !this.placement) {
      targets.push({
        target_type: 'course',
        course_id: this.course.id,
        bot_id: v.bot_id,
        page_pattern: v.page_pattern || 'course-view-*',
        block_region: blockRegion,
        block_weight: blockWeight,
      });
    }
    for (const group of this.activityGroups) {
      const botId = this.effectiveGroupTutor(group);
      for (const activity of group.activities) {
        const existingPlacement = activity.placement || null;
        const existingPlacementId = this.placementId(existingPlacement);

        if (!group.enabled) {
          if (existingPlacementId) {
            requests.push(this.lms.deletePlacement(existingPlacementId));
          }
          continue;
        }

        if (existingPlacementId) {
          const nextBotId = botId || null;
          const currentBotId = existingPlacement?.bot_id || null;
          if (nextBotId !== currentBotId) {
            requests.push(this.lms.updatePlacement(existingPlacementId, { bot_id: nextBotId }));
          }
          continue;
        }

        targets.push({
          target_type: 'module',
          course_id: this.course.id,
          bot_id: botId || null,
          cmid: activity.cmid,
          modtype: activity.modtype,
          page_pattern: this.activityPagePattern(activity.modtype),
          block_region: blockRegion,
          block_weight: blockWeight,
        });
      }
    }

    if (targets.length === 0 && requests.length === 0) {
      if (this.hasExistingPlacementState()) {
        this.saving = false;
        this.dialogRef.close(false);
        return;
      }
      this.saving = false;
      this.errorDetail = this.t.instant('pages.tutors.placements.lms.createDialog.selectTargetError');
      return;
    }

    if (targets.length === 1 && targets[0].target_type === 'course') {
      const target = targets[0];
      const payload: RemotePlacementCreate = {
        course_id: target.course_id,
        bot_id: target.bot_id || null,
        page_pattern: target.page_pattern || 'course-view-*',
        block_region: target.block_region,
        block_weight: target.block_weight,
      };
      this.lms.createSitePlacement(this.site.id, payload).subscribe({
        next: () => {
          if (requests.length === 0) {
            this.saving = false;
            this.dialogRef.close(true);
            return;
          }
          forkJoin(requests).subscribe({
            next: () => {
              this.saving = false;
              this.dialogRef.close(true);
            },
            error: (err) => {
              this.saving = false;
              this.errorDetail = this.resolveError(err);
              this.toastr.danger(this.errorDetail || '', '');
            },
          });
        },
        error: (err) => {
          this.saving = false;
          this.errorDetail = this.resolveError(err);
          this.toastr.danger(this.errorDetail || '', '');
        },
      });
      return;
    }

    if (targets.length > 0) {
      requests.push(this.lms.createSitePlacementsBulk(this.site.id, targets));
    }

    forkJoin(requests).subscribe({
      next: (results) => {
        const failedBulk = results.find((result: any) => result?.failed_count > 0) as any;
        if (failedBulk) {
          this.saving = false;
          this.errorDetail = this.t.instant(
            'pages.tutors.placements.lms.createDialog.bulkFailure',
            { count: failedBulk.failed_count },
          );
          this.toastr.danger(this.errorDetail, '');
          return;
        }
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        this.errorDetail = this.resolveError(err);
        this.toastr.danger(this.errorDetail || '', '');
      },
    });
  }
}
